import { useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  EQUIVALENCE_OUTCOMES,
  RECOGNITION_STATES,
  VERIFICATION_METHODS,
  type EquivalenceOutcome,
  type Page,
  type RecognitionApplication,
  type VerificationMethod,
  type VerifyRecognitionResult,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/**
 * Foreign-qualification recognition
 * (packages/modules/m29-tertiary-tvet/src/routes/recognition.ts). Applying
 * and paying the assessment fee are citizen self-service; this console runs
 * the SQA_ASSESSOR side: logging institution-contact verification steps,
 * recording the equivalence determination, and issuing/revoking the
 * QR-verifiable DocVault recognition certificate.
 */
export function Recognition() {
  const { persona } = useAuth();
  const isAssessor = persona?.roles.includes('SQA_ASSESSOR') ?? false;
  const [tab, setTab] = useState<'applications' | 'verify'>('applications');

  return (
    <div>
      <PageHeader title="Qualification Recognition" subtitle="Foreign-qualification equivalence assessment for the Seychelles Qualifications Authority." />
      <Tabs tabs={[{ key: 'applications', label: 'Applications' }, { key: 'verify', label: 'Verify Certificate' }]} value={tab} onChange={setTab} />
      {!isAssessor && tab === 'applications' ? <ErrorNote message="Listing recognition applications requires the SQA Assessor role." /> : null}
      {tab === 'applications' ? <ApplicationsTab isAssessor={isAssessor} /> : <VerifyTab isAssessor={isAssessor} />}
    </div>
  );
}

function ApplicationsTab({ isAssessor }: { isAssessor: boolean }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<RecognitionApplication>>(isAssessor ? `/api/v1/m29/recognition/applications?${qs.toString()}` : null);

  if (!isAssessor) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-56" placeholder="Search applicant NIN / name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {RECOGNITION_STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No recognition applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Applicant</th>
                <th className="table-head">Award</th>
                <th className="table-head">Institution / country</th>
                <th className="table-head">Fee</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(a.id)}>
                  <td className="table-cell">
                    <div className="font-medium">{a.applicantFullName}</div>
                    <div className="font-mono text-xs text-slate-400">{a.applicantNin}</div>
                  </td>
                  <td className="table-cell">{a.awardTitle}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {a.awardingInstitution}, {a.awardingCountry}
                  </td>
                  <td className="table-cell text-xs">{scr(a.feeAmount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? <ApplicationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function ApplicationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<RecognitionApplication>(`/api/v1/m29/recognition/applications/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showLogStep, setShowLogStep] = useState(false);
  const [method, setMethod] = useState<VerificationMethod>('EMAIL');
  const [institutionContact, setInstitutionContact] = useState('');
  const [stepNotes, setStepNotes] = useState('');
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [respondOutcome, setRespondOutcome] = useState<'CONFIRMED' | 'DISCREPANCY' | 'NO_RESPONSE'>('CONFIRMED');
  const [respondNotes, setRespondNotes] = useState('');
  const [showDetermine, setShowDetermine] = useState(false);
  const [determineOutcome, setDetermineOutcome] = useState<EquivalenceOutcome>('RECOGNIZED');
  const [equivalenceLevel, setEquivalenceLevel] = useState('');
  const [determineNotes, setDetermineNotes] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [showRevoke, setShowRevoke] = useState(false);

  const app = detailQ.data;
  const steps = app?.verificationSteps ?? [];
  const allResolved = steps.length > 0 && steps.every((s) => s.outcome !== 'PENDING');

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/recognition/applications/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowLogStep(false);
      setRespondingId(null);
      setShowDetermine(false);
      setShowRevoke(false);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Recognition application" onClose={onClose} wide>
      {detailQ.loading || !app ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={app.state} />
            <span className="font-semibold">{app.applicantFullName}</span>
            <span className="font-mono text-xs text-slate-400">{app.applicantNin}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Award title</dt>
              <dd className="font-medium">{app.awardTitle}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Awarding institution</dt>
              <dd className="font-medium">{app.awardingInstitution}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Country</dt>
              <dd className="font-medium">{app.awardingCountry}</dd>
            </div>
            {app.awardYear ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Award year</dt>
                <dd className="font-medium">{app.awardYear}</dd>
              </div>
            ) : null}
          </dl>
          {app.equivalenceOutcome ? (
            <p className="text-xs text-slate-500">
              Determination: <StatusBadge status={app.equivalenceOutcome} /> {app.equivalenceLevel ? `— ${app.equivalenceLevel}` : ''}
            </p>
          ) : null}
          {app.certSerial ? (
            <p className="text-xs text-slate-500">
              Certificate <span className="font-mono">{app.certSerial}</span> issued {formatDateTime(app.certIssuedAt)}
              {app.certRevokedAt ? ` — revoked ${formatDateTime(app.certRevokedAt)}` : ''}
            </p>
          ) : null}

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Verification steps</div>
              {app.state === 'UNDER_REVIEW' ? (
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowLogStep((s) => !s)}>
                  + Log step
                </button>
              ) : null}
            </div>
            {showLogStep ? (
              <div className="mb-3 grid grid-cols-3 items-end gap-2 rounded-lg border border-slate-200 p-2.5">
                <Field label="Method" className="!mb-0">
                  <select className="field-input !py-1 text-xs" value={method} onChange={(e) => setMethod(e.target.value as VerificationMethod)}>
                    {VERIFICATION_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Institution contact" className="!mb-0">
                  <input className="field-input !py-1 text-xs" value={institutionContact} onChange={(e) => setInstitutionContact(e.target.value)} />
                </Field>
                <Field label="Notes" className="!mb-0">
                  <input className="field-input !py-1 text-xs" value={stepNotes} onChange={(e) => setStepNotes(e.target.value)} />
                </Field>
                <button
                  className="btn-primary col-span-3 !py-1.5 text-xs"
                  disabled={busy}
                  onClick={() => act('/verification-steps', { method, institutionContact: institutionContact || undefined, notes: stepNotes || undefined })}
                >
                  Log verification step
                </button>
              </div>
            ) : null}
            {steps.length === 0 ? (
              <p className="text-xs text-slate-400">No verification steps logged yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {steps.map((s) => (
                  <li key={s.id} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span>
                        {s.method} {s.institutionContact ? `— ${s.institutionContact}` : ''}
                      </span>
                      <StatusBadge status={s.outcome} />
                    </div>
                    {s.notes ? <p className="mt-1 text-slate-500">{s.notes}</p> : null}
                    {s.outcome === 'PENDING' ? (
                      respondingId === s.id ? (
                        <div className="mt-2 flex items-end gap-2">
                          <Field label="Outcome" className="!mb-0">
                            <select
                              className="field-input !w-40 !py-1 text-xs"
                              value={respondOutcome}
                              onChange={(e) => setRespondOutcome(e.target.value as 'CONFIRMED' | 'DISCREPANCY' | 'NO_RESPONSE')}
                            >
                              <option value="CONFIRMED">CONFIRMED</option>
                              <option value="DISCREPANCY">DISCREPANCY</option>
                              <option value="NO_RESPONSE">NO_RESPONSE</option>
                            </select>
                          </Field>
                          <Field label="Notes" className="!mb-0 flex-1">
                            <input className="field-input !py-1 text-xs" value={respondNotes} onChange={(e) => setRespondNotes(e.target.value)} />
                          </Field>
                          <button
                            className="btn-primary !px-2.5 !py-1 text-xs"
                            disabled={busy}
                            onClick={() => act(`/verification-steps/${s.id}/respond`, { outcome: respondOutcome, notes: respondNotes || undefined })}
                          >
                            Record
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary !mt-2 !px-2 !py-0.5 text-[11px]" onClick={() => setRespondingId(s.id)}>
                          Record outcome
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {app.state === 'UNDER_REVIEW' && allResolved ? (
              <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => setShowDetermine((s) => !s)}>
                Record determination
              </button>
            ) : null}
            {app.state === 'DETERMINED' && app.equivalenceOutcome !== 'NOT_RECOGNIZED' ? (
              <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/certify')}>
                Issue certificate
              </button>
            ) : null}
            {app.state === 'CERTIFIED' && !app.certRevokedAt ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowRevoke((s) => !s)}>
                Revoke certificate
              </button>
            ) : null}
            {['SUBMITTED', 'UNDER_REVIEW'].includes(app.state) ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/withdraw')}>
                Withdraw
              </button>
            ) : null}
          </div>

          {showDetermine ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Outcome">
                  <select className="field-input" value={determineOutcome} onChange={(e) => setDetermineOutcome(e.target.value as EquivalenceOutcome)}>
                    {EQUIVALENCE_OUTCOMES.map((o) => (
                      <option key={o} value={o}>
                        {o.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
                {determineOutcome !== 'NOT_RECOGNIZED' ? (
                  <Field label="Equivalence level">
                    <input className="field-input" value={equivalenceLevel} onChange={(e) => setEquivalenceLevel(e.target.value)} placeholder="e.g. Bachelor's degree equivalent" />
                  </Field>
                ) : null}
              </div>
              <Field label="Notes">
                <textarea className="field-input" rows={2} value={determineNotes} onChange={(e) => setDetermineNotes(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowDetermine(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary !px-2.5 !py-1 text-xs"
                  disabled={busy || determineNotes.trim().length < 3 || (determineOutcome !== 'NOT_RECOGNIZED' && !equivalenceLevel.trim())}
                  onClick={() =>
                    act('/determine', {
                      outcome: determineOutcome,
                      equivalenceLevel: determineOutcome !== 'NOT_RECOGNIZED' ? equivalenceLevel : undefined,
                      notes: determineNotes,
                    })
                  }
                >
                  Confirm determination
                </button>
              </div>
            </div>
          ) : null}

          {showRevoke ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Revoke reason" className="!mb-0 flex-1">
                <input className="field-input" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || revokeReason.trim().length < 3} onClick={() => act('/revoke-certificate', { reason: revokeReason })}>
                Confirm
              </button>
              <button className="btn-secondary" onClick={() => setShowRevoke(false)}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function VerifyTab({ isAssessor }: { isAssessor: boolean }) {
  const [certSerial, setCertSerial] = useState('');
  const [result, setResult] = useState<VerifyRecognitionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.get<VerifyRecognitionResult>(`/api/v1/m29/verify/recognition/${encodeURIComponent(certSerial)}`);
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">sqa:verify-recognition</h2>
      <p className="mb-4 text-xs text-slate-500">
        {isAssessor ? 'Staff lookup — allowed outright for SQA Assessor.' : 'Any other caller requires an affirmative C7 ConsentHub check for this applicant first.'}
      </p>
      {error ? <ErrorNote message={error} /> : null}
      <div className="flex items-end gap-2">
        <Field label="Certificate serial" className="!mb-0 flex-1">
          <input className="field-input" value={certSerial} onChange={(e) => setCertSerial(e.target.value)} placeholder="e.g. QRC-000123" />
        </Field>
        <button className="btn-primary" disabled={busy || certSerial.trim().length < 3} onClick={check}>
          {busy ? 'Checking…' : 'Check'}
        </button>
      </div>
      {result ? (
        <div className="mt-4 border-t border-slate-100 pt-3 text-sm">
          {!result.found ? (
            <p className="text-slate-500">No recognition certificate found for that serial.</p>
          ) : (
            <dl className="space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-slate-500">Valid</dt>
                <dd>
                  <StatusBadge status={result.valid ? 'VALID' : 'REVOKED'} />
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Award</dt>
                <dd className="font-medium">{result.awardTitle}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Country</dt>
                <dd className="font-medium">{result.awardingCountry}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Equivalence</dt>
                <dd className="font-medium">
                  {result.equivalenceOutcome} {result.equivalenceLevel ? `— ${result.equivalenceLevel}` : ''}
                </dd>
              </div>
            </dl>
          )}
        </div>
      ) : null}
    </div>
  );
}
